import { Button, Spin, Input, Modal } from "antd";
import axios from "axios";
import { useState, useEffect } from "react";
import Sidebar from "../../GlobalFiles/Sidebar";
import { API_BASE_URL } from "../../../../../api";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, X, FileText, User, Stethoscope, Group } from "lucide-react";
import DoctorSelect from "../../../../../Components/Dropdowns/DoctorSelect";
import { toast } from "react-toastify";
import TestUnitInput from "../../../../../Components/report/TestUnitInput";
import NormalValueInput from "../../../../../Components/report/NormalValueInput";
import { BsPerson } from "react-icons/bs";
import isTextTypeReport from "../../../../../helper/isTextTypeReport";

const CompletePartial = () => {
    const [loading, stLoading] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const [selectedReport, setSelectedReport] = useState(null);
    const [currentModal, setCurrentModal] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const [formData, setFormdata] = useState({
        patient: "",
        testOrder: "",
        doctor: "",
        preparedBy: "",
        comment: "",
        results: []
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;

    useEffect(() => {
        console.log("Location state:", state.report);

        if (state && state.report) {
            const rpt = state.report;
            setSelectedReport(rpt);
            const patient = rpt.patient || {};
            setFormdata({
                patient: patient._id || patient || "",
                testOrder: rpt.testOrder?._id || rpt._id || "",
                doctor: rpt.doctor?._id || rpt.doctor || null,
                preparedBy: rpt.preparedBy?._id || rpt.preparedBy || null,
                comment: rpt.comment || "",
                results: rpt?.results ? rpt.results.map(r => {
                    const isTextType = isTextTypeReport(r?.test?.testGroup?.superGroup);
                    const seededValue = r.value || "";
                    const seededUnit = isTextType ? "" : (r.testUnit || "");
                    const seededNormalValue = isTextType ? "" : (r.normalValue || "");

                    const hasValue = !!(seededValue && String(seededValue).trim() !== "");
                    const seededStatus = r.status || (isTextType
                        ? (hasValue ? 'completed' : 'pending')
                        : (hasValue && seededUnit && seededNormalValue ? 'completed' : 'pending'));

                    return {
                        _id: r._id,
                        test: r.test,
                        value: seededValue,
                        testUnit: seededUnit,
                        normalValue: seededNormalValue,
                        status: seededStatus
                    };
                }) : [],
            });
        } else {
            navigate(-1);
        }
    }, [state, navigate]);

    const handleSave = async () => {
        // Clean up doctor and preparedBy fields to avoid empty string
        const cleanFormData = {
            ...formData,
            doctor: formData.doctor || null,
            preparedBy: formData.preparedBy || null,
        };
        console.log("Saving report: ", cleanFormData);
        setIsSaving(true);

        try {
            const res = await axios.patch(`${API_BASE_URL}/reports/${selectedReport._id}`, cleanFormData);
            if (res.status === 200) {
                setSelectedReport(res.data.report);
                navigate(".", {
                    replace: true,
                    state: { ...location.state, report: res.data.report },
                });
                toast.success("Report updated successfully.", { toastId: "update-success", autoClose: 2000 });
            } else {
                toast.error("Error updating report. Please try again.", { toastId: "update-error" });
            }
        } catch (error) {
            console.error("Error saving report:", error);
            toast.error("Error saving report. Please try again.", { toastId: "save-error" });
        } finally {
            setIsSaving(false);
        }
    };

    const setFormTestValue = (idx, fieldName, newValue) => {
        setFormdata((prevData) => ({
            ...prevData,
            results: prevData.results.map((r, i) => {
                if (i !== idx) return r;

                const updated = { ...r, [fieldName]: newValue };

                const refTest = selectedReport?.results?.[idx];
                const isTextType = isTextTypeReport(refTest?.test?.testGroup?.superGroup);

                const hasValue = !!(updated.value && String(updated.value).trim() !== "");
                const completed = isTextType
                    ? hasValue
                    : (hasValue && updated.testUnit && updated.normalValue);

                console.log("Updated test result:", updated, "with field:", fieldName, "and newValue:", newValue);
                return {
                    ...updated,
                    status: completed ? "completed" : "pending",
                };
            })
        }));
    };

    const getGroupedTests = () => {
        if (!selectedReport) return null;

        const grouped = selectedReport?.results.reduce((acc, curr) => {
            const group = curr.test?.category || "Other";
            if (!acc[group]) {
                acc[group] = [];
            }
            curr.status = (formData.results || []).find(r => (r.test === curr._id || r.test === curr))?.status || "pending";
            acc[group].push(curr);
            return acc;
        }, {});
        return grouped;
    };

    return (
        <div>
            <div className="flex min-h-screen bg-gray-50 overflow-x-hidden">
                <Sidebar onCollapse={setSidebarCollapsed} />
                <div className={`flex-1 min-w-0 p-3 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'
                    }`}>
                    <div className="flex-1 min-w-0 p-3 ml-40">
                        {/* Header Section */}
                        <div className="mb-6 relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-5 rounded-3xl"></div>
                            <div className="relative z-10 p-5">
                                <div className="flex items-center space-x-4 mb-2">
                                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                                        <FileText className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                                            Complete Incomplete Report
                                        </h1>
                                        <p className="text-slate-600 text-lg font-medium">Complete your previous incomplete report</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 p-12 text-center">
                                    <Spin size="large" className="mb-4" />
                                    <p className="text-slate-600 text-lg font-medium">Loading report data...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Test Result Section */}
                                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden">
                                    <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4 flex justify-between items-center">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                                <FileText className="text-white text-lg" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white">Test Results</h2>
                                            {selectedReport && <div className="text-white/80">{formData?.results?.filter(t => t.status === "completed").length || 0}/{selectedReport?.tests?.length || 0} completed</div>}
                                        </div>
                                        <div className="flex items-center space-x-3 gap-2">
                                            {selectedReport && <Button
                                                onClick={() => setCurrentModal("patient-info")}
                                                className="bg-white/20 hover:bg-white/30 text-white font-semibold px-2 rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-200"
                                            >
                                                <BsPerson className="inline-block m-2 my-auto" /> Patient Info
                                            </Button>}
                                            {selectedReport && <Button
                                                onClick={() => setCurrentModal("grouped-test")}
                                                className="bg-white/20 hover:bg-white/30 text-white font-semibold px-2 rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-200"
                                            >
                                                <Group className="inline-block m-2 my-auto" /> Grouped Test
                                            </Button>}
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        <div className="overflow-x-auto h-[300px] overflow-y-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b-2 border-gray-100">
                                                        <th className="text-left py-4 px-4 font-semibold text-slate-700   w-[5%]">SL</th>
                                                        <th className="text-left py-4 px-4 font-semibold text-slate-700   w-[20%]">Test</th>
                                                        <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[20%]">Result</th>
                                                        <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[15%]">Test Unit</th>
                                                        <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[20%]">Normal</th>
                                                        <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[15%]">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="h-full">
                                                    {selectedReport && selectedReport?.results && selectedReport?.results?.map((t, i) => {
                                                        const isTextType = isTextTypeReport(t?.test?.testGroup?.superGroup);
                                                        return (
                                                            <tr className="border-b border-gray-50 hover:bg-gray-100 transition-colors rounded-lg" key={i}>
                                                                <td className="py-2 px-4 font-medium text-slate-600">
                                                                    {i + 1}
                                                                </td>
                                                                <td className="py-2 px-4 font-medium text-slate-600">
                                                                    {t?.test?.title || "N/A"}
                                                                </td>
                                                                <td className="py-2 px-4" colSpan={isTextType ? 3 : 1}>
                                                                    {isTextType ? (
                                                                        <Input.TextArea
                                                                            placeholder="Enter Value"
                                                                            autoSize={{ minRows: 3, maxRows: 3 }}
                                                                            value={formData?.results[i]?.value || ""}
                                                                            onChange={(e) => setFormTestValue(i, "value", e.target.value)}
                                                                            className="border-2 border-gray-200 rounded-lg hover:border-blue-300 focus:border-blue-500 transition-all duration-200"
                                                                        />
                                                                    ) : (
                                                                        <Input
                                                                            placeholder="Enter Value"
                                                                            value={formData?.results[i]?.value || ""}
                                                                            onChange={(e) => setFormTestValue(i, "value", e.target.value)}
                                                                            className="border-2 border-gray-200 rounded-lg hover:border-blue-300 focus:border-blue-500 transition-all duration-200"
                                                                        />
                                                                    )}
                                                                </td>
                                                                {!isTextType && (
                                                                    <>
                                                                        <td className="py-4 px-4">
                                                                            <TestUnitInput
                                                                                value={formData?.results[i]?.testUnit || ""}
                                                                                onChange={nt => setFormTestValue(i, "testUnit", nt)}
                                                                            />
                                                                        </td>
                                                                        <td className="py-4 px-4 text-center text-slate-600">
                                                                            <NormalValueInput
                                                                                value={formData?.results[i]?.normalValue || ""}
                                                                                onChange={nv => setFormTestValue(i, "normalValue", nv)}
                                                                            />
                                                                        </td>
                                                                    </>
                                                                )}
                                                                <td className="py-4 px-4 text-center text-slate-600">
                                                                    {(formData?.results[i]?.status) === "completed" ? (
                                                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                                                            Complete
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                                                                            Pending
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                            {(!selectedReport?.results || selectedReport?.results.length === 0) && <h1 className="text-center w-full p-5 text-red-600 font-semibold">No tests found.</h1>}
                                        </div>
                                    </div>
                                </div>

                                {/* Comments Section: Layout is flex Comment title, textarea*/}
                                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 px-6 py-3">
                                    <div className="flex items-center space-x-4 mb-2">
                                        <span className="text-lg font-semibold text-slate-700">Report Comment</span>
                                    </div>
                                    <Input.TextArea
                                        value={formData.comment}
                                        onChange={(e) => setFormdata({ ...formData, comment: e.target.value })}
                                        autoSize={{ minRows: 3, maxRows: 4 }}
                                        className="w-full border-2 border-gray-200 rounded-lg p-2 focus:border-blue-500 transition-all duration-200"
                                        placeholder="Enter your comments here..."
                                    />
                                </div>

                                {/* Action Section */}
                                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 px-8 py-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4 flex-1 gap-5">
                                            <span className="text-lg font-semibold text-slate-700">Prepared by</span>
                                            <div className="flex-1">
                                                <DoctorSelect
                                                    selectedId={formData.preparedBy || undefined}
                                                    onSelectID={(id) => setFormdata((prev) => ({ ...prev, preparedBy: id }))}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            <Button
                                                type="primary"
                                                onClick={handleSave}
                                                disabled={isSaving}
                                                className={`bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 py-4 rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-lg h-auto ${isSaving ? 'opacity-60 cursor-wait' : ''}`}
                                            >
                                                {isSaving ? 'Saving...' : 'Save Report'}
                                            </Button>

                                            <Button
                                                onClick={() => navigate(-1)}
                                                className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white font-semibold px-6 py-4 rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-lg h-auto"
                                            >
                                                Exit
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* select-test-order modal removed: CompletePartial works with a single report passed via location.state.report */}

                <Modal
                    open={currentModal === "patient-info"}
                    onCancel={() => setCurrentModal("")}
                    onOk={() => setCurrentModal("")}
                    closeIcon={false}
                    title={
                        <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                    <User className="text-white text-lg" />
                                </div>
                                <h2 className="text-2xl font-bold text-white">Patient Information</h2>
                            </div>
                        </div>
                    }
                >
                    <div className="p-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
                                <span className="font-semibold text-slate-700">ID</span>
                                <span className="bg-white px-4 py-1 rounded-lg font-medium text-slate-600 shadow-sm">
                                    {selectedReport?.patient?.patientID || "N/A"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                                <span className="font-semibold text-slate-700">Name</span>
                                <span className="bg-white px-4 py-1 rounded-lg font-medium text-slate-600 shadow-sm">
                                    {selectedReport?.patient?.patientName || "Not Found"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                                <span className="font-semibold text-slate-700">Gender</span>
                                <span className="bg-white px-4 py-1 rounded-lg font-medium text-slate-600 shadow-sm">
                                    {selectedReport?.patient?.gender || "Not Found"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                                <span className="font-semibold text-slate-700">Age</span>
                                <span className="bg-white px-4 py-1 rounded-lg font-medium text-slate-600 shadow-sm">
                                    {selectedReport?.patient?.age || "Not Found"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl">
                                <span className="font-semibold text-slate-700">Ref by</span>
                                <span className="bg-white px-4 py-1 rounded-lg font-medium text-slate-600 shadow-sm truncate">
                                    {selectedReport?.agent?.name || "Not Specified"}
                                </span>
                            </div>
                        </div>
                    </div>
                </Modal>

                <Modal
                    open={currentModal === "grouped-test"}
                    onCancel={() => setCurrentModal("")}
                    onOk={() => setCurrentModal("")}
                    title={
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                    <Stethoscope className="text-white text-lg" />
                                </div>
                                <h2 className="text-2xl font-bold text-white">Test Groups</h2>
                            </div>
                        </div>
                    }
                >
                    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden">
                        <div className="p-6">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b-2 border-gray-100">
                                            <th className="text-left py-4 px-4 font-semibold text-slate-700">Test Group</th>
                                            <th className="text-left py-4 px-4 font-semibold text-slate-700">Test Name</th>
                                            <th className="text-center py-4 px-4 font-semibold text-slate-700">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getGroupedTests() ? (
                                            Object.entries(getGroupedTests()).map(([groupName, tests]) =>
                                                tests.map((test, testIdx) => (
                                                    <tr
                                                        key={`${groupName}-${testIdx}`}
                                                        className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                                                    >
                                                        {testIdx === 0 && (
                                                            <td
                                                                rowSpan={tests.length}
                                                                className="py-4 px-4 font-medium text-slate-600 align-top"
                                                            >
                                                                {groupName}
                                                            </td>
                                                        )}
                                                        <td className="py-4 px-4 text-slate-600">
                                                            {test?.test?.title || test?.test || "N/A"}
                                                        </td>
                                                        <td className="py-4 px-4 text-center">
                                                            {test.status === "completed" ? (
                                                                <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                                                    <Check className="w-3 h-3 mr-1" />
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                                                                    <X className="w-3 h-3 mr-1" />
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )) : (<tr><td colSpan={3} className="text-center p-5 text-red-600 font-semibold">No test order selected.</td></tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default CompletePartial;